import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  DSAlert,
  DSBackLink,
  DSBadge,
  DSButton,
  DSCheckbox,
  DSIcon,
  DSInput,
  DSModal,
  DSSelect,
  DSTextarea
} from '../design-system/primitives';
import { previewHoleResult, computeNet, describeNet, toDisplayHandicap, UNPLAYED_HOLE_RESULT } from '../utils/pingolfScoring';
import { formatScoreAbbreviated, formatDigitGroups } from '../utils/formatScore';
import { useEventDetails } from '../hooks/useEventDetails';
import PinGolfScore, { PinGolfScoreLegend } from '../components/PinGolfScore';
import PinGolfStats from '../components/PinGolfStats';
import './pingolfShared.css';
import './PinGolfCard.css';
import { personName } from '../utils/personName';

const MISSED_OPTION_VALUE = 'missed';
const FINAL_SCORE_SAVE_DEBOUNCE_MS = 600;
const HOLES_PER_SECTION = 9;

function PinGolfCard() {
  const { eventId, cardId } = useParams();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const {
    event,
    loading,
    isHost,
    pingolfCards,
    loadingPingolfCards,
    setSelectedPingolfCardId,
    savingPingolfResultHoleId,
    submittingPingolfCard,
    pingolfDraftHoleInputs, setPingolfDraftHoleInputs,
    correctingPingolfHoleId,
    showPingolfSubmitConfirmModal, setShowPingolfSubmitConfirmModal,
    pingolfCorrectionDraft, setPingolfCorrectionDraft,
    pingolfPurchaseError,
    handleSavePingolfHoleResult,
    handleClearPingolfHoleResult,
    pingolfHoleSaveError, setPingolfHoleSaveError,
    pingolfAbandonTargetCardId, setPingolfAbandonTargetCardId,
    abandoningPingolfCardId,
    handleAbandonPingolfCard,
    setPreservedPingolfHoleInputId,
    handleSubmitPingolfCard,
    handleCorrectPingolfHoleResult
  } = useEventDetails(eventId, currentUser, isAdmin, navigate);

  const [editingHoleId, setEditingHoleId] = useState(null);

  // Opening a hole's entry modal marks that hole's draft as "being edited" in useEventDetails,
  // so the sync effect that re-seeds drafts from the server after every save leaves it alone.
  // Without this, each debounced save's response overwrites whatever you have typed since -
  // type "100", it saves, add a "0", and the reply snaps the box back to "100".
  const openHoleEntry = (holeId) => {
    setPingolfHoleSaveError(null);
    setPreservedPingolfHoleInputId(holeId);
    setEditingHoleId(holeId);
  };

  const closeHoleEntry = () => {
    setPreservedPingolfHoleInputId(null);
    setPingolfHoleSaveError(null);
    setEditingHoleId(null);
  };
  useEffect(() => {
    const parsedCardId = parseInt(cardId, 10);
    if (Number.isInteger(parsedCardId)) {
      setSelectedPingolfCardId(parsedCardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  const finalScoreSaveTimers = useRef({});
  useEffect(() => () => {
    Object.values(finalScoreSaveTimers.current).forEach(clearTimeout);
  }, []);

  if (loading) return <div className="loading">Loading card...</div>;
  if (!event) return <div className="error">Event not found</div>;

  const isPingolfEvent = event.event_metadata?.pinball_format === 'pingolf';
  if (!isPingolfEvent) return <div className="error">This page is only available for PinGolf events.</div>;

  const canManagePingolfConfig = isAdmin || isHost;
  const isPingolfFinalized = Boolean(event.event_metadata?.pingolf?.finalized_at);
  const isPingolfCanceled = Boolean(event.event_metadata?.pingolf?.canceled_at);

  const playerDisplayName = (card) => (
    personName(card) || 'Player'
  );
  const getPingolfCardStatusVariant = (status) => {
    if (status === 'submitted') return 'success';
    if (status === 'abandoned') return 'warning';
    return 'info';
  };

  const selectedPingolfCard = pingolfCards.find(card => String(card.id) === String(cardId)) || null;

  if (loadingPingolfCards && !selectedPingolfCard) {
    return (
      <div className="pingolf-card-page">
        <div className="pingolf-card-back-row">
          <DSBackLink to={`/events/${eventId}`}>Back to Event</DSBackLink>
        </div>
        <p>Loading card...</p>
      </div>
    );
  }

  if (!selectedPingolfCard) {
    return (
      <div className="pingolf-card-page">
        <div className="pingolf-card-back-row">
          <DSBackLink to={`/events/${eventId}`}>Back to Event</DSBackLink>
        </div>
        <DSAlert variant="error" appearance="outline">Card not found.</DSAlert>
      </div>
    );
  }

  const holes = event.pingolf_holes || [];
  const selectedPingolfHoleResults = new Map((selectedPingolfCard.hole_results || []).map(hole => [hole.event_hole_id, hole]));
  const hasCompleteDraftCard = selectedPingolfCard.status === 'draft'
    && holes.length > 0
    && holes.every(hole => selectedPingolfHoleResults.has(hole.id));
  // Verification is no longer a gate - a complete card is submittable on its own.
  const canSubmitSelectedDraftCard = Boolean(
    hasCompleteDraftCard
      && !submittingPingolfCard
      && !isPingolfFinalized
      && !isPingolfCanceled
  );
  const isOwnCard = selectedPingolfCard.user_id === currentUser?.id;
  const isEditableCard = selectedPingolfCard.status === 'draft' && isOwnCard;
  const holesPlayed = selectedPingolfHoleResults.size;
  const playingHandicap = selectedPingolfCard.playing_handicap;
  const cardNet = computeNet(selectedPingolfCard.total_score, playingHandicap);
  // Server-computed, so the warning below quotes the same number finalization will use.
  const unplayedHoleCount = selectedPingolfCard.unplayed_holes ?? 0;
  const isCountingCard = Boolean(selectedPingolfCard.is_counting_card);

  const getDraft = (holeId) => pingolfDraftHoleInputs[holeId] || { reached: null, crossBall: '', doubled: false, finalTotal: '' };

  const setDraftField = (holeId, patch) => {
    setPingolfDraftHoleInputs(prev => ({ ...prev, [holeId]: { ...getDraft(holeId), ...patch } }));
  };

  const saveHoleDraft = (hole, draft) => {
    const maxBalls = hole.max_balls || 3;
    const preview = previewHoleResult(draft, hole.target_score, maxBalls);
    if (!preview) {
      // An incomplete or invalid draft - "Missed target" with no final score yet, a cleared
      // score box, a final score at or above the target - must not leave a previously saved
      // result standing, or the card shows a score the modal doesn't. Erase it so what is
      // stored always matches what is on screen.
      if (selectedPingolfHoleResults.has(hole.id)) {
        handleClearPingolfHoleResult(selectedPingolfCard.id, hole.id);
      }
      return;
    }

    const payload = preview.reached
      ? { reached: true, crossBall: preview.crossBall, doubled: preview.doubled }
      : { reached: false, finalTotal: preview.finalTotal };

    handleSavePingolfHoleResult(selectedPingolfCard.id, hole.id, hole.hole_number, payload);
  };

  const handleResultSelect = (hole, value) => {
    const patch = value === ''
      ? { reached: null, crossBall: '', doubled: false, finalTotal: '' }
      : value === MISSED_OPTION_VALUE
        ? { reached: false, crossBall: '' }
        : { reached: true, crossBall: Number(value) };

    setDraftField(hole.id, patch);
    // Every incomplete state - including the blank "Select which ball..." option - is handled
    // by saveHoleDraft, which clears any stored result when the draft can't produce a score.
    saveHoleDraft(hole, { ...getDraft(hole.id), ...patch });

    // Choosing "Missed target" always needs a second answer, so put the cursor in it rather
    // than making the player find the field. Deferred a tick because the input is disabled
    // until the reached:false state has actually rendered.
    if (value === MISSED_OPTION_VALUE) {
      setTimeout(() => {
        const input = document.getElementById(`pingolf-final-score-${hole.id}`);
        if (input) input.focus();
      }, 0);
    }
  };

  const handleDoubledToggle = (hole, checked) => {
    const patch = { doubled: checked };
    setDraftField(hole.id, patch);
    saveHoleDraft(hole, { ...getDraft(hole.id), ...patch });
  };

  const handleFinalScoreChange = (hole, value) => {
    // Store raw digits; the field renders them comma-grouped. Anything non-numeric the user
    // pastes in is dropped here rather than being parsed away later.
    const digitsOnly = String(value).replace(/\D/g, '');
    setDraftField(hole.id, { finalTotal: digitsOnly });

    clearTimeout(finalScoreSaveTimers.current[hole.id]);
    finalScoreSaveTimers.current[hole.id] = setTimeout(() => {
      saveHoleDraft(hole, getDraft(hole.id));
    }, FINAL_SCORE_SAVE_DEBOUNCE_MS);
  };

  const handleFinalScoreBlur = (hole) => {
    clearTimeout(finalScoreSaveTimers.current[hole.id]);
    saveHoleDraft(hole, getDraft(hole.id));
  };

  const openCorrection = (hole) => {
    const holeResult = selectedPingolfHoleResults.get(hole.id);
    const persistedReport = holeResult && (holeResult.reached === true || holeResult.reached === false)
      ? (holeResult.reached
        ? { reached: true, crossBall: holeResult.cross_ball, doubled: Boolean(holeResult.doubled) }
        : { reached: false, finalTotal: holeResult.final_total })
      : null;

    setPingolfCorrectionDraft({
      cardId: selectedPingolfCard.id,
      holeId: hole.id,
      existingReport: persistedReport,
      reached: persistedReport ? persistedReport.reached : null,
      crossBall: persistedReport?.reached ? String(persistedReport.crossBall) : '',
      doubled: persistedReport?.reached ? persistedReport.doubled : false,
      finalTotal: persistedReport && !persistedReport.reached ? String(persistedReport.finalTotal) : '',
      reason: ''
    });
  };

  const canCorrectHole = (hole) => selectedPingolfCard.status === 'submitted'
    && canManagePingolfConfig
    && selectedPingolfHoleResults.has(hole.id)
    && !isPingolfFinalized
    && !isPingolfCanceled;

  const sections = [];
  for (let i = 0; i < holes.length; i += HOLES_PER_SECTION) {
    sections.push(holes.slice(i, i + HOLES_PER_SECTION));
  }
  const sectionLabel = (index) => (index === 0 ? 'Out' : (index === 1 ? 'In' : `Holes ${index * HOLES_PER_SECTION + 1}-${index * HOLES_PER_SECTION + sections[index].length}`));

  const editingHole = editingHoleId ? holes.find(hole => hole.id === editingHoleId) : null;

  // A league night's event title already reads "<Season> - Week N", so prefer the structured
  // season/week fields and fall back to the raw title for a standalone PinGolf event.
  const cardContextLabel = event.season_name
    ? [event.season_name, event.week_number ? `Week ${event.week_number}` : null].filter(Boolean).join(' · ')
    : event.title;
  const cardDate = event.event_date ? new Date(event.event_date) : null;
  const cardDateLabel = cardDate && !Number.isNaN(cardDate.getTime())
    ? cardDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  // Ordered so the row reads as the arithmetic it describes: score, minus handicap, equals net -
  // then how far through the round you are. Each opens an explainer on tap.
  const scorecardStats = [
    {
      key: 'score',
      label: 'Score',
      value: <PinGolfScore value={selectedPingolfCard.total_score} emphasis />,
      detailTitle: 'Score',
      detailBody: 'Your raw card for the round: the sum of every hole result against par. This is what you actually shot, before your handicap is applied. Lower is better, so under par shows green.'
    },
    ...(Number.isFinite(playingHandicap) ? [{
      key: 'hcp',
      label: 'HCP',
      value: <PinGolfScore value={toDisplayHandicap(playingHandicap)} emphasis />,
      detailTitle: 'Playing Handicap',
      detailBody: "The strokes your carried average applies to this card, shown the way they act on your score. Shoot over par on average and this is negative - strokes come off, which is the allowance that lets you race a veteran. Shoot under par on average and it is positive, adding strokes back, because you are good enough to spot the field. It is set before you play from previous rounds only, so the card you are shooting now never changes the handicap applied to it. A brand-new player starts at 0."
    }] : []),
    ...(cardNet !== null ? [{
      key: 'net',
      label: 'Net',
      value: <PinGolfScore value={cardNet} emphasis />,
      detailTitle: 'Net',
      detailBody: `Your score with your playing handicap taken off: ${describeNet(selectedPingolfCard.total_score, playingHandicap)}. Net is what the leaderboard ranks on, and lower is better.`
    }] : []),
    {
      key: 'thru',
      label: 'Thru',
      value: `${holesPlayed}/${holes.length}`,
      detailTitle: 'Thru',
      detailBody: `How many of the ${holes.length} holes you have finished and recorded. Your score covers only the holes played so far, so it keeps moving until you are thru all ${holes.length}.`
    }
  ];

  return (
    <div className="pingolf-card-page">
      <div className="pingolf-card-back-row">
        <DSBackLink to={`/events/${eventId}`}>Back to Event</DSBackLink>
      </div>

      <div className="pingolf-scorecard-header">
        <div className="pingolf-scorecard-header-identity">
          <DSBadge variant={getPingolfCardStatusVariant(selectedPingolfCard.status)} appearance="outline">{selectedPingolfCard.status}</DSBadge>
          {/* The player's name, not "My Card" - a scorecard is identified by who shot it, and
              this page is also how a host or admin views someone else's card. */}
          <h1>{playerDisplayName(selectedPingolfCard)}</h1>
          {/* Which league, which night, and when. A card viewed later out of context is
              otherwise just a column of numbers. */}
          <p className="pingolf-scorecard-context">{cardContextLabel}</p>
          {cardDateLabel && <p className="pingolf-scorecard-context-date">{cardDateLabel}</p>}
        </div>
        <PinGolfStats stats={scorecardStats} variant="compact" />
      </div>

      {pingolfPurchaseError && (
        <DSAlert variant="error" appearance="outline">{pingolfPurchaseError}</DSAlert>
      )}

      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex}>
          {sections.length > 1 && <p className="pingolf-scorecard-section-label">{sectionLabel(sectionIndex)}</p>}
          <div className="pingolf-scorecard-table-wrap">
            <table className="pingolf-scorecard-table">
              <thead>
                <tr>
                  <th className="pingolf-scorecard-row-label">Hole</th>
                  {section.map(hole => <th key={hole.id}>{hole.hole_number}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th className="pingolf-scorecard-row-label">Par</th>
                  {section.map(hole => <td key={hole.id}>{hole.max_balls || 3}</td>)}
                </tr>
                <tr>
                  <th className="pingolf-scorecard-row-label">Score</th>
                  {section.map(hole => {
                    const holeResult = selectedPingolfHoleResults.get(hole.id);
                    const hasResult = holeResult && holeResult.hole_result !== null && holeResult.hole_result !== undefined;
                    const canEditCell = isEditableCard || canCorrectHole(hole);
                    const isSaving = savingPingolfResultHoleId === hole.id || correctingPingolfHoleId === hole.id;

                    return (
                      <td key={hole.id} className="pingolf-scorecard-score-cell">
                        {canEditCell ? (
                          <button
                            type="button"
                            className="pingolf-scorecard-score-btn"
                            onClick={() => (isEditableCard ? openHoleEntry(hole.id) : openCorrection(hole))}
                            disabled={isSaving}
                          >
                            {isSaving
                              ? '...'
                              : <PinGolfScore value={hasResult ? holeResult.hole_result : null} doubled={holeResult && holeResult.doubled} />}
                          </button>
                        ) : (
                          <PinGolfScore value={hasResult ? holeResult.hole_result : null} doubled={holeResult && holeResult.doubled} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Machine-by-machine list. The grid above is the scorecard - good for reading the round
          at a glance, useless for answering "which hole is the machine I'm standing at?". Play
          is often out of order, so this is the entry surface: find the machine, tap the row. */}
      <div className="pingolf-hole-rows">
        {holes.map((hole) => {
          const holeResult = selectedPingolfHoleResults.get(hole.id);
          const hasResult = holeResult && holeResult.hole_result !== null && holeResult.hole_result !== undefined;
          const canEditRow = isEditableCard || canCorrectHole(hole);
          const isSaving = savingPingolfResultHoleId === hole.id || correctingPingolfHoleId === hole.id;
          const RowTag = canEditRow ? 'button' : 'div';

          return (
            <RowTag
              key={hole.id}
              type={canEditRow ? 'button' : undefined}
              className={`pingolf-hole-row${canEditRow ? ' is-tappable' : ''}${hasResult ? '' : ' is-unplayed'}`}
              onClick={canEditRow ? () => (isEditableCard ? openHoleEntry(hole.id) : openCorrection(hole)) : undefined}
              disabled={canEditRow ? isSaving : undefined}
            >
              <span className="pingolf-hole-row-number">{hole.hole_number}</span>
              <span className="pingolf-hole-row-body">
                <span className="pingolf-hole-row-machine">{hole.machine_name}</span>
                <span className="pingolf-hole-row-meta">
                  Target {formatScoreAbbreviated(hole.target_score)} · {hole.max_balls || 3} balls
                </span>
              </span>
              <span className="pingolf-hole-row-score">
                {isSaving
                  ? '...'
                  : hasResult
                    ? <PinGolfScore value={holeResult.hole_result} doubled={holeResult.doubled} emphasis />
                    : <span className="pingolf-hole-row-empty">{canEditRow ? 'Enter' : '-'}</span>}
              </span>
            </RowTag>
          );
        })}
      </div>

      <PinGolfScoreLegend className="pingolf-scorecard-legend" />

      {/* One primary action slot that changes with the card's state. While holes are still
          missing there is nothing to submit, so the only meaningful action is walking away
          from the card; once every hole is in, that slot becomes Submit. */}
      {selectedPingolfCard.status === 'draft' && isOwnCard && (
        hasCompleteDraftCard ? (
          <DSButton
            appearance="solid"
            tone="primary"
            onClick={() => setShowPingolfSubmitConfirmModal(true)}
            disabled={!canSubmitSelectedDraftCard}
          >
            Submit Card
          </DSButton>
        ) : (
          <DSButton
            appearance="outline"
            tone="danger"
            onClick={() => setPingolfAbandonTargetCardId(selectedPingolfCard.id)}
            disabled={Boolean(abandoningPingolfCardId)}
          >
            Abandon Card
          </DSButton>
        )
      )}

      {editingHole && (() => {
        const maxBalls = editingHole.max_balls || 3;
        const draft = getDraft(editingHole.id);
        const preview = previewHoleResult(draft, editingHole.target_score, maxBalls);
        const selectValue = draft.reached === true ? String(draft.crossBall) : (draft.reached === false ? MISSED_OPTION_VALUE : '');
        const isSaving = savingPingolfResultHoleId === editingHole.id;
        // "Missed target" picked but no usable final score yet. Distinct from "nothing selected",
        // which is a legitimate state to leave the hole in - this one is a half-finished answer
        // that would silently record nothing, so it has to be called out.
        const needsFinalScore = draft.reached === false && !preview;
        // The validation message lives in the field's own help slot rather than an alert block,
        // so showing it swaps text in place instead of growing the modal. Server-side rejections
        // land in the same slot for the same reason.
        const enteredFinalScore = parseInt(draft.finalTotal, 10);
        const finalScoreHelp = (() => {
          if (needsFinalScore) {
            return Number.isInteger(enteredFinalScore) && enteredFinalScore >= editingHole.target_score
              ? `Must be below the target of ${formatScoreAbbreviated(editingHole.target_score)}.`
              : 'Required - enter the score you finished on.';
          }
          // Always a non-empty string: the help line is part of the reserved height, so letting
          // it fall away would shrink the slot the moment a valid score is typed.
          return pingolfHoleSaveError || 'The score you finished on.';
        })();
        const hasFieldError = needsFinalScore || Boolean(pingolfHoleSaveError);
        // The sentence under the score. Two jobs: turn the vs-par number into words, since
        // "-2 is good and +2 is bad" is backwards from how a number normally reads, and echo
        // the answers it came from so a wrong entry is visible without reopening the fields.
        // While the score is still blank it says what is missing instead.
        const previewDetail = (() => {
          if (!preview) {
            if (draft.reached === false) return 'Enter your final score to see the hole score.';
            return 'Pick a result above to see the hole score.';
          }
          const parPhrase = preview.holeResult === 0
            ? 'Even par'
            : `${Math.abs(preview.holeResult)} ${preview.holeResult < 0 ? 'under' : 'over'} par`;
          const from = preview.reached
            ? `reached on ball ${preview.crossBall} of ${maxBalls}${preview.doubled ? ', doubled' : ''}`
            : `missed, finished on ${formatScoreAbbreviated(preview.finalTotal)}`;
          return `${parPhrase}, ${from}`;
        })();

        return (
          <DSModal title={`Hole ${editingHole.hole_number} - ${editingHole.machine_name}`} size="sm" hideClose onClose={closeHoleEntry}>
            <p className="pingolf-details-subtitle">Target {formatScoreAbbreviated(editingHole.target_score)} (par {maxBalls})</p>
            <div className="pingolf-hole-entry-fields">
              <DSSelect
                label="Result"
                value={selectValue}
                onChange={(e) => handleResultSelect(editingHole, e.target.value)}
              >
                <option value="">Select which ball...</option>
                {Array.from({ length: maxBalls }, (_, i) => i + 1).map(ballNumber => (
                  <option key={ballNumber} value={ballNumber}>Ball {ballNumber}</option>
                ))}
                <option value={MISSED_OPTION_VALUE}>Missed target</option>
              </DSSelect>
              {/* Both follow-up fields stay mounted in a fixed slot and are hidden (not removed)
                  when they do not apply to the selected result. A greyed-out field that can never
                  be filled in reads as something the player is failing to do, so it goes away -
                  but unmounting it resized the modal on every selection, which moved the controls
                  out from under the user's finger mid-entry. Hiding keeps the height constant. */}
              <div className={`pingolf-hole-entry-slot${draft.reached === true ? '' : ' is-hidden'}`}>
                <DSCheckbox
                  id={`pingolf-doubled-${editingHole.id}`}
                  label="Doubled the target"
                  checked={draft.reached === true && Boolean(draft.doubled)}
                  disabled={draft.reached !== true}
                  onChange={(e) => handleDoubledToggle(editingHole, e.target.checked)}
                />
              </div>
              <div className={`pingolf-hole-entry-slot${draft.reached === false ? '' : ' is-hidden'}`}>
                <DSInput
                  id={`pingolf-final-score-${editingHole.id}`}
                  label="Final score"
                  state={hasFieldError ? 'error' : 'inactive'}
                  helpText={finalScoreHelp}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Final score"
                  value={draft.reached === false ? formatDigitGroups(draft.finalTotal) : ''}
                  disabled={draft.reached !== false}
                  onChange={(e) => handleFinalScoreChange(editingHole, e.target.value)}
                  onBlur={() => handleFinalScoreBlur(editingHole)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                />
              </div>
              {/* Reserves its own height so the result appearing never nudges the buttons.
                  On its own the bare "-2" was just a number sitting under the form with no
                  label - players had to be told what it was. It now says what the figure is
                  ("Hole score"), what it means in words ("2 under par"), and which of their
                  answers produced it, so the arithmetic is never something to take on faith. */}
              <div className="pingolf-hole-status-row">
                <div className="pingolf-hole-status-text">
                  <span className="pingolf-hole-status-label">
                    Hole score
                    <span className={`pingolf-saving-indicator${isSaving ? '' : ' is-idle'}`}>Saving...</span>
                  </span>
                  <span className="pingolf-hole-status-detail">{previewDetail}</span>
                </div>
                <PinGolfScore
                  className="pingolf-hole-status-value"
                  value={preview ? preview.holeResult : null}
                  doubled={Boolean(preview && preview.doubled)}
                  emphasis
                />
              </div>
            </div>
            {/* Own class, not .form-actions - that one is defined unscoped in PinballEventForm.css
                and EventsHub.css, which add a 2px border-top and force a column layout on mobile.
                Two buttons in a dialog belong on one row at every width. */}
            <div className="pingolf-modal-actions">
              <DSButton appearance="outline" tone="neutral" onClick={closeHoleEntry}>
                Cancel
              </DSButton>
              {/* Not disabled while saving. Pressing Done blurs the final-score box, which fires
                  its save, which set isSaving and disabled this button before the click landed -
                  so the first press did nothing and it took two taps. The save is already in
                  flight and completes (and refetches) on its own, so closing here is safe. */}
              <DSButton
                appearance="solid"
                tone="primary"
                disabled={needsFinalScore}
                title={needsFinalScore ? 'Enter your final score first' : undefined}
                onClick={closeHoleEntry}
              >
                Done
              </DSButton>
            </div>
          </DSModal>
        );
      })()}

      {pingolfAbandonTargetCardId && (
        <DSModal title="Abandon this card?" size="md" onClose={() => setPingolfAbandonTargetCardId(null)}>
          {/* Abandoning no longer erases the card - it keeps its unplayed-hole penalties and,
              under the most-recent-card rule, still supersedes whatever came before it. The score
              it locks in is the one thing a player must not miss, so it leads as a number rather
              than sitting in the middle of a paragraph nobody finishes reading. */}
          <div className="pingolf-abandon-outcome">
            <div className="pingolf-abandon-outcome-label">This card is recorded as</div>
            <PinGolfScore value={selectedPingolfCard.projected_total_score} emphasis />
            <div className="pingolf-abandon-outcome-math">
              {unplayedHoleCount} unplayed {unplayedHoleCount === 1 ? 'hole' : 'holes'} x +{UNPLAYED_HOLE_RESULT}
            </div>
          </div>

          <ul className="pingolf-abandon-points">
            {isCountingCard && (
              <li>
                <DSIcon name="trophy" size="sm" tone="muted" />
                <span>
                  <strong>It stays the card that counts</strong>, replacing any card you played earlier.
                </span>
              </li>
            )}
            <li>
              <DSIcon name="dollar" size="sm" tone="muted" />
              <span><strong>No refund.</strong> Your money stays in the pot.</span>
            </li>
            <li>
              <DSIcon name="golf" size="sm" tone="muted" />
              <span>To beat this score you have to buy a new card and play it out.</span>
            </li>
          </ul>
          <div className="pingolf-modal-actions">
            <DSButton
              appearance="outline"
              tone="neutral"
              onClick={() => setPingolfAbandonTargetCardId(null)}
              disabled={Boolean(abandoningPingolfCardId)}
            >
              Keep playing
            </DSButton>
            <DSButton
              appearance="solid"
              tone="danger"
              onClick={handleAbandonPingolfCard}
              disabled={Boolean(abandoningPingolfCardId)}
            >
              {abandoningPingolfCardId ? 'Abandoning...' : 'Abandon Card'}
            </DSButton>
          </div>
        </DSModal>
      )}

      {showPingolfSubmitConfirmModal && (
        <DSModal title="Submit Card?" size="md" onClose={() => setShowPingolfSubmitConfirmModal(false)}>
          <p className="pingolf-details-subtitle">Submitted cards lock for players.</p>
          <div className="form-actions">
            <DSButton
              appearance="outline"
              tone="neutral"
              onClick={() => setShowPingolfSubmitConfirmModal(false)}
              disabled={submittingPingolfCard}
            >
              Close
            </DSButton>
            <DSButton
              appearance="solid"
              tone="primary"
              onClick={() => handleSubmitPingolfCard(selectedPingolfCard.id)}
              disabled={!canSubmitSelectedDraftCard || submittingPingolfCard}
            >
              {submittingPingolfCard ? 'Submitting...' : 'Submit Card'}
            </DSButton>
          </div>
        </DSModal>
      )}

      {pingolfCorrectionDraft.cardId && pingolfCorrectionDraft.holeId && (() => {
        const correctionHole = holes.find(h => h.id === pingolfCorrectionDraft.holeId);
        const correctionMaxBalls = correctionHole?.max_balls || 3;
        const resetCorrectionDraft = () => setPingolfCorrectionDraft({
          cardId: null, holeId: null, existingReport: null, reached: null, crossBall: '', doubled: false, finalTotal: '', reason: ''
        });
        const existingReport = pingolfCorrectionDraft.existingReport;
        const correctionSelectValue = pingolfCorrectionDraft.reached === true
          ? String(pingolfCorrectionDraft.crossBall)
          : (pingolfCorrectionDraft.reached === false ? MISSED_OPTION_VALUE : '');
        return (
          <DSModal title="Correct Hole Result" size="md" onClose={resetCorrectionDraft}>
            <p className="pingolf-details-subtitle">
              Current result: <strong>
                {existingReport
                  ? (existingReport.reached
                    ? `Reached on ball ${existingReport.crossBall}${existingReport.doubled ? ' (doubled)' : ''}`
                    : `Missed, final score ${formatScoreAbbreviated(existingReport.finalTotal)}`)
                  : 'none'}
              </strong>
            </p>
            <div className="pingolf-correction-modal-fields">
              <DSSelect
                label="Corrected result"
                value={correctionSelectValue}
                onChange={(eventChange) => {
                  const value = eventChange.target.value;
                  const patch = value === ''
                    ? { reached: null, crossBall: '' }
                    : value === MISSED_OPTION_VALUE
                      ? { reached: false, crossBall: '' }
                      : { reached: true, crossBall: Number(value) };
                  setPingolfCorrectionDraft((current) => ({ ...current, ...patch }));
                }}
              >
                <option value="">Select which ball...</option>
                {Array.from({ length: correctionMaxBalls }, (_, i) => i + 1).map(ballNumber => (
                  <option key={ballNumber} value={ballNumber}>Ball {ballNumber}</option>
                ))}
                <option value={MISSED_OPTION_VALUE}>Missed target</option>
              </DSSelect>
              {pingolfCorrectionDraft.reached === true && (
                <DSCheckbox
                  id="pingolf-correction-doubled"
                  label="Doubled the target"
                  checked={Boolean(pingolfCorrectionDraft.doubled)}
                  onChange={(eventChange) => setPingolfCorrectionDraft((current) => ({ ...current, doubled: eventChange.target.checked }))}
                />
              )}
              {pingolfCorrectionDraft.reached === false && (
                <DSInput
                  id="pingolf-correction-final-score"
                  type="number"
                  min="0"
                  required
                  placeholder="Final score"
                  value={pingolfCorrectionDraft.finalTotal}
                  onChange={(eventChange) => setPingolfCorrectionDraft((current) => ({ ...current, finalTotal: eventChange.target.value }))}
                />
              )}
              <DSTextarea
                id="pingolf-correction-reason"
                label="Correction note (optional)"
                value={pingolfCorrectionDraft.reason}
                onChange={(eventChange) => setPingolfCorrectionDraft((current) => ({ ...current, reason: eventChange.target.value }))}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <DSButton appearance="outline" tone="neutral" onClick={resetCorrectionDraft}>
                Cancel
              </DSButton>
              <DSButton appearance="solid" tone="primary" onClick={handleCorrectPingolfHoleResult} disabled={correctingPingolfHoleId === pingolfCorrectionDraft.holeId}>
                {correctingPingolfHoleId === pingolfCorrectionDraft.holeId ? 'Saving...' : 'Apply Correction'}
              </DSButton>
            </div>
          </DSModal>
        );
      })()}
    </div>
  );
}

export default PinGolfCard;
